import React, { useCallback, useEffect, useState } from 'react';

import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Slider from '@material-ui/core/Slider';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import CropOutlinedIcon from '@material-ui/icons/CropOutlined';
import ZoomInOutlinedIcon from '@material-ui/icons/ZoomInOutlined';
import PhotoOutlinedIcon from '@material-ui/icons/PhotoOutlined';
import RotateLeftOutlinedIcon from '@material-ui/icons/RotateLeftOutlined';
import RestoreOutlinedIcon from '@material-ui/icons/RestoreOutlined';

import clsx from 'clsx';

import { useStyles } from './Style';

export const MODOS = {
    CORTADA: 'CORTADA',
    AMPLIADA: 'AMPLIADA',
    ORIGINAL: 'ORIGINAL',
};

const ESCALA_AMPLIADA = 1.5;

const VisualizadorCartao = ({ urlImagemCorte, urlImagemCartao, height, onLoadComplete, toolbarExtra }) => {
    const classes = useStyles();

    const [modo, setModo] = useState(MODOS.CORTADA);
    const [zoom, setZoom] = useState(1);
    const [rotacionado, setRotacionado] = useState(false);
    const [carregando, setCarregando] = useState(true);

    const url = modo === MODOS.ORIGINAL ? urlImagemCartao : urlImagemCorte;

    useEffect(() => {
        setCarregando(Boolean(url));
    }, [url]);

    const handleReset = useCallback(() => {
        setModo(MODOS.CORTADA);
        setZoom(1);
        setRotacionado(false);
    }, []);

    const handleToggleRotacao = useCallback(() => {
        setRotacionado(estado => !estado);
    }, []);

    useEffect(() => {
        const onKeyDown = event => {
            if (!event.altKey) return;

            const key = event.key.toLowerCase();

            if (key === 'q') { event.preventDefault(); setModo(MODOS.CORTADA); }
            if (key === 'w') { event.preventDefault(); setModo(MODOS.AMPLIADA); }
            if (key === 'a') { event.preventDefault(); setModo(MODOS.ORIGINAL); }
            if (key === 's') { event.preventDefault(); handleReset(); }
            if (key === 'z') { event.preventDefault(); handleToggleRotacao(); }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [handleReset, handleToggleRotacao]);

    const handleLoad = () => {
        setCarregando(false);
        if (onLoadComplete) onLoadComplete();
    };

    const escala = zoom * (modo === MODOS.AMPLIADA ? ESCALA_AMPLIADA : 1);

    return (
        <div className={classes.visualizador}>
            <div className={classes.visualizadorToolbar}>
                <div className={classes.visualizadorToolbarAcoes}>
                    <Tooltip title="Imagem cortada (Alt+Q)">
                        <IconButton size="small" color={modo === MODOS.CORTADA ? 'primary' : 'default'} onClick={() => setModo(MODOS.CORTADA)}>
                            <CropOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Imagem ampliada (Alt+W)">
                        <IconButton size="small" color={modo === MODOS.AMPLIADA ? 'primary' : 'default'} onClick={() => setModo(MODOS.AMPLIADA)}>
                            <ZoomInOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Imagem original (Alt+A)">
                        <IconButton size="small" color={modo === MODOS.ORIGINAL ? 'primary' : 'default'} onClick={() => setModo(MODOS.ORIGINAL)}>
                            <PhotoOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Girar 180° (Alt+Z)">
                        <IconButton size="small" color={rotacionado ? 'primary' : 'default'} onClick={handleToggleRotacao}>
                            <RotateLeftOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Restaurar (Alt+S)">
                        <IconButton size="small" onClick={handleReset}>
                            <RestoreOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </div>

                <div className={classes.visualizadorZoom}>
                    <Typography variant="caption" color="textSecondary">Zoom</Typography>
                    <Slider
                        className={classes.visualizadorSlider}
                        value={zoom}
                        min={1}
                        max={2}
                        step={0.1}
                        onChange={(_, novoZoom) => setZoom(novoZoom)}
                    />
                    <Typography variant="caption" color="textSecondary">{Math.round(escala * 100)}%</Typography>
                </div>

                <div className={classes.visualizadorToolbarExtra}>
                    {toolbarExtra}
                </div>
            </div>

            <div className={classes.visualizadorImagemBox} style={{ maxHeight: height || undefined }}>
                {carregando && <LinearProgress className={classes.visualizadorLoading} />}

                {url ?
                    <img
                        src={url}
                        alt="Cartão de prova"
                        className={clsx(classes.visualizadorImagem, { [classes.visualizadorImagemRotacionada]: rotacionado })}
                        style={{ width: `${escala * 100}%` }}
                        onLoad={handleLoad}
                        onError={() => setCarregando(false)}
                    />
                    :
                    <div className={classes.visualizadorVazio}>
                        <Typography variant="body2" color="textSecondary">Imagem não disponível.</Typography>
                    </div>
                }
            </div>
        </div>
    );
};

export default VisualizadorCartao;
